import React, { useMemo, useState, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'
import LetterForm from '../../../components/2025/LetterForm'
import { useQuery } from '@apollo/client'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { QUERY } from '../../../graphql'
import LetterBox from '../../../components/2025/LetterBox'
import { SpringContext } from '../../../context/SpringContext'
import { ThemeContext } from '../../../context/2025/ThemeContext'
import { useViewport } from '../../../hook/useViewport'

const Header = React.lazy(() => import('../../../components/2025/Header'))
const Footer = React.lazy(() => import('../../../components/2025/Footer'))
// Removed FullScreenLoader import as loading state isn't explicitly shown in the return body for this component
// const FullScreenLoader = React.lazy(() => import('../../../components/FullScreenLoader'))

// Keep styled SearchContainer as is
const SearchContainer = styled.div`
  p {
    text-align: left;
    padding-left: 30px;
    color: #000000;
    font-size: 1.3em;
    position: absolute;
    line-height: 60px;
    margin-bottom: 0;
    width: 100%;
  }
  position: relative;

  svg {
    top: 36px; /* Adjust if needed based on input height/padding */
    color: #000000;
    right: 22px;
    transition: transform 330ms;
    pointer-events: none; /* Prevent SVG from blocking input clicks */
  }

  input {
    font-size: 1.2rem; /* Adjusted font-size slightly */
    height: 60px;
    border-radius: 10px;
    padding: 5px 45px 5px 20px; /* Added padding-right for icon */
    margin-bottom: 20px;
    margin-top: 20px;
    outline: 2px solid
      ${(props) => (props.hasError ? 'red' : props.isDarkMode ? '#DAFF01' : '#9167F0')};
    /* display: ${(props) =>
      props.submitted ? 'none' : 'block'}; */ /* Removed display:none based on props */
    display: block; /* Default display */
    box-sizing: border-box; /* Ensure padding is included in width */
  }

  input:hover {
    outline: 4px solid
      ${(props) => (props.hasError ? 'red' : props.isDarkMode ? '#DAFF01' : '#9167F0')};
  }
  input:focus {
    outline: 4px solid
      ${(props) => (props.hasError ? 'red' : props.isDarkMode ? '#DAFF01' : '#9167F0')};
  }

  @media (min-width: 640px) {
    #email {
      /* This ID might not be relevant for the search input */
      width: 480px;
    }
  }
`

export default function Index() {
  const [gradsSearch, setGradsSearch] = useState('')
  const spring = useContext(SpringContext)
  const { isDarkMode } = useContext(ThemeContext)
  const inputRef = useRef(null)
  // --- Step 1: Add ref to track initial load ---
  const isInitialLoad = useRef(true)
  const { width } = useViewport()
  const isMobile = width <= 640

  // useEffect(() => {
  //   if (spring != null && spring !== undefined) {
  //     spring.setCurrentValue(0).setAtRest()
  //     spring.setEndValue(0)
  //   }
  // }, [spring])

  const { data, loading } = useQuery(QUERY.LETTERS_QUERY, {
    notifyOnNetworkStatusChange: true, // Keep if needed
    variables: {
      where: {
        createdAt_gt: '2025-03-31',
        createdAt_lt: '2025-12-31',
      },
      order: {
        createdAt: 'asc',
      },
    },
  })

  const filteredLetters = useMemo(() => {
    if (!data?.letters) {
      return {}
    }
    let letters = data?.letters || []
    // update the data based on the search if there is any search in data.senders or data.recipients
    if (gradsSearch) {
      letters = letters.filter((letter) => {
        const { sender, recipient } = letter
        return (
          sender.toLowerCase().includes(gradsSearch.toLowerCase()) ||
          recipient.toLowerCase().includes(gradsSearch.toLowerCase())
        )
      })
    }

    return letters
  }, [data, gradsSearch])

  // --- Step 2: Modify the focus effect ---
  useEffect(() => {
    // This effect now only runs its logic once after the initial load completes
    if (!loading && isInitialLoad.current) {
      // Instead of focusing, we just mark the initial load as complete
      isInitialLoad.current = false

      // DO NOT FOCUS HERE:
      // requestAnimationFrame(() => {
      //   requestAnimationFrame(() => {
      //     if (inputRef.current) { // Check ref exists just in case
      //         inputRef.current.focus({ preventScroll: true });
      //     }
      //   });
      // });
    }
  }, [loading]) // Only depends on the initial loading state

  // --- Handler for search input change ---
  const handleSearchChange = (e) => {
    setGradsSearch(e.target.value)
    // No need to set an interaction flag here as focus isn't tied to re-loading
  }

  const backgroundColors = {
    form: isDarkMode ? 'bg-black' : 'bg-white',
    letters: isDarkMode ? 'bg-[#222222]' : 'bg-light-grey-25',
    footer: isDarkMode ? 'bg-black' : 'bg-white',
  }

  const textColors = {
    heading: isDarkMode ? 'text-lime' : 'text-lavender',
    subheading: isDarkMode ? 'text-white' : 'text-black',
    light: isDarkMode ? 'text-white' : 'text-black',
    accent: isDarkMode ? 'text-lime' : 'text-lavender',
  }

  return (
    <div
      className={`absolute w-full min-h-full bg-black flex flex-col items-center justify-start pb-20 lg:pb-0  ${
        isDarkMode ? 'bg-black' : 'bg-white'
      } transition-colors duration-300 `}
    >
      {/* Conditionally render Header */}
      {Header && <Header />}

      {/* Top Section with Title and Form */}
      <section
        className={`w-full ${isDarkMode ? 'bg-black' : 'bg-white'} transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-16 lg:py-32 flex flex-col items-center justify-center gap-16 lg:gap-32'>
          {' '}
          {/* Adjusted padding/gap */}
          <div className='relative w-full flex flex-col items-left justify-center px-[8px] lg:px-[48px]'>
            {' '}
            {/* Adjusted padding */}
            <div className='relative grid grid-cols-1 lg:grid-cols-2 gap-14'>
              {/* Left side: Title and Subtitle */}
              <div className='relative flex flex-col justify-center'>
                {' '}
                {/* Added justify-center */}
                <div className='flex flex-col text-lg font-light'>
                  {' '}
                  {/* Removed text-black here, use context */}
                  <h1
                    className={`uppercase ${
                      isMobile ? 'heading-1-mobile' : 'heading-1 !leading-[120px]'
                    } ${
                      textColors.heading // Use text color from context
                    } `}
                    style={{ pointerEvents: 'none' }}
                  >
                    Send
                    <br />
                    your
                    <br />
                    wishes!
                  </h1>
                  <h4 className={`mt-4 lg:pr-[20%] ${textColors.subheading} heading-4-bold`}>
                    Let's send our encouragement to this year's winners. May 2, 2025
                  </h4>
                </div>
              </div>
              {/* Right side: Letter Form */}
              <LetterForm />
            </div>
          </div>
        </div>
      </section>

      {/* Search and Count Section */}
      <section
        className={`w-full ${
          isDarkMode ? 'bg-[#222222]' : 'bg-light-grey-25'
        } transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-16 lg:py-32 flex flex-col items-center justify-center gap-16 lg:gap-32'>
          {' '}
          {/* Adjusted padding/gap */}
          <div className='grid grid-cols-1 lg:grid-cols-2 w-full items-center gap-8 lg:gap-16'>
            {' '}
            {/* Added gap */}
            {/* Message Count */}
            <div>
              {loading ? ( // Show loading indicator based on query state
                <p className={`${textColors.light} body-text`}>Loading messages...</p>
              ) : filteredLetters?.length === 0 ? (
                <p className={`${textColors.light} body-text`}>
                  {gradsSearch.trim().length > 0
                    ? 'No messages found matching your search.'
                    : 'Be the first to leave a message!'}
                </p>
              ) : filteredLetters?.length === 1 ? (
                <p className={`${textColors.light} body-text`}>
                  There is <span className={textColors.accent}>1</span> message
                  {gradsSearch.trim().length > 0 ? ' matching your search' : ''}.
                </p>
              ) : (
                // More than 1 message
                <p className={`${textColors.light} body-text`}>
                  There are <span className={textColors.accent}>{filteredLetters?.length}</span>{' '}
                  messages{gradsSearch.trim().length > 0 ? ' matching your search' : ''}.
                </p>
              )}
            </div>
            {/* Search Input */}
            <SearchContainer
              className='flex relative w-full items-center justify-end' // Use items-center, justify-end
              isDarkMode={isDarkMode}
            >
              <input
                type='text'
                id='gname' // Consider more specific ID like 'letterSearch'
                name='gname'
                ref={inputRef}
                placeholder='Search by sender or recipient name' // Updated placeholder
                value={gradsSearch}
                className={`body-text w-full lg:w-full ${
                  isDarkMode
                    ? 'bg-white text-black placeholder-gray-500'
                    : 'bg-white text-black placeholder-gray-500'
                }`} // Adjusted classes, maybe placeholder color
                onChange={handleSearchChange} // Use specific handler
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                  }
                }}
                // Ensure no autoFocus={true}
              />
              {/* Search Icon SVG */}
              <svg
                width='26' /* Adjusted size */
                height='26'
                viewBox='0 0 26 26'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='absolute' /* Centered icon */
              >
                <g id='Frame 76'>
                  <path
                    id='Vector'
                    d='M0.93934 22.9393C0.353553 23.5251 0.353553 24.4749 0.93934 25.0607C1.52513 25.6464 2.47487 25.6464 3.06066 25.0607L0.93934 22.9393ZM22.5 9.7C22.5 13.1242 19.7242 15.9 16.3 15.9V18.9C21.381 18.9 25.5 14.781 25.5 9.7H22.5ZM16.3 15.9C12.8758 15.9 10.1 13.1242 10.1 9.7H7.1C7.1 14.781 11.219 18.9 16.3 18.9V15.9ZM10.1 9.7C10.1 6.27584 12.8758 3.5 16.3 3.5V0.5C11.219 0.5 7.1 4.61898 7.1 9.7H10.1ZM16.3 3.5C19.7242 3.5 22.5 6.27583 22.5 9.7H25.5C25.5 4.61898 21.381 0.5 16.3 0.5V3.5ZM3.06066 25.0607L7.56066 20.5607L5.43934 18.4393L0.93934 22.9393L3.06066 25.0607ZM7.56066 20.5607L12.0607 16.0607L9.93934 13.9393L5.43934 18.4393L7.56066 20.5607Z'
                    fill={'#000'} /* Icon color based on mode */
                  />
                </g>
              </svg>
            </SearchContainer>
          </div>
        </div>
      </section>

      {/* Letters Grid Section */}
      <section className={`w-full ${backgroundColors.letters} transition-colors duration-300`}>
        <div className='max-w-[1440px] mx-auto w-full px-6 lg:px-[48px] py-16 lg:pb-32'>
          {' '}
          {/* Adjusted padding */}
          <TransitionGroup className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-14'>
            {' '}
            {/* Adjusted gap */}
            {/* Show letters only if not loading */}
            {!loading &&
              filteredLetters.length > 0 &&
              filteredLetters.map((letter, index) => (
                <CSSTransition key={letter.id || index} timeout={500} classNames='item'>
                  {' '}
                  {/* Use letter.id if available */}
                  <LetterBox
                    // key prop should be on the outer element (CSSTransition)
                    letter={letter}
                    index={index} // index might not be needed if key is stable id
                    isDarkMode={isDarkMode}
                  />
                </CSSTransition>
              ))}
          </TransitionGroup>
        </div>
      </section>

      {/* Footer Section */}
      <div
        className={`relative w-full flex flex-col items-center justify-center w-full ${
          isDarkMode ? 'bg-black' : 'bg-light-grey-25'
        }`}
      >
        <div
          className={`relative bottom-0 w-full pt-16 px-[16px] lg:px-[48px] bg-black max-w-[1440px] ${
            isDarkMode ? 'bg-black' : 'bg-light-grey-25'
          }`}
        >
          {Footer && <Footer />}
        </div>
      </div>
    </div>
  )
}
